import React, { useEffect, useRef } from 'react';

// Atleta exibido no seletor de participantes do rodízio (pool do grupo).
export interface AtletaSelecionavelDialog {
  id: string;
  usuario?: { id?: string; nome?: string; imagemUrl?: string | null } | null;
}

export interface AthleteSelectorDialogProps {
  open: boolean;
  atletas: AtletaSelecionavelDialog[];
  busca: string;
  onBuscaChange: (value: string) => void;
  onClose: () => void;
  onSelect: (atleta: AtletaSelecionavelDialog) => void;
  /** Atleta atualmente ocupando o slot em edição — recebe o badge "Selecionado". */
  selectedAtletaId?: string | null;
  /** Atletas já usados em outros slots da partida — desabilitados (badge "Em uso"). */
  disabledAtletaIds?: string[];
}

/**
 * Seletor de atleta para os slots de participante do rodízio (Rei/Rainha, Super 8).
 * Espelha o layout de {@link TeamSelectorDialog}: busca no topo + lista com botão
 * "Selecionar" por item. O atleta do slot atual mostra "Selecionado"; atletas já
 * escalados em outros slots aparecem "Em uso" (sem botão).
 */
const AthleteSelectorDialog: React.FC<AthleteSelectorDialogProps> = ({
  open,
  atletas,
  busca,
  onBuscaChange,
  onClose,
  onSelect,
  selectedAtletaId,
  disabledAtletaIds = [],
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      document.body.classList.add('modal-open');
      modalRef.current?.focus();
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [open]);

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const filtered = atletas.filter((a) =>
    (a.usuario?.nome ?? 'Atleta').toLowerCase().includes(busca.toLowerCase()),
  );

  return (
    <>
      <div className="modal-backdrop fade show" id="select-athlete" />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        ref={modalRef}
        onMouseDown={handleBackdrop}
        style={{ overflow: 'auto' }}
      >
        <div
          className="modal-dialog modal-dialog-scrollable"
          role="document"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Selecionar atleta</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Buscar..."
                value={busca}
                onChange={(e) => onBuscaChange(e.target.value)}
              />
              <ul className="list-group">
                {filtered.map((atleta) => {
                  const nome = atleta.usuario?.nome ?? 'Atleta';
                  const selecionado = selectedAtletaId === atleta.id;
                  const emUso = !selecionado && disabledAtletaIds.includes(atleta.id);
                  return (
                    <li
                      key={atleta.id}
                      className={`list-group-item d-flex justify-content-between align-items-center ${emUso ? 'opacity-50' : ''}`}
                    >
                      <div className="d-flex align-items-center" style={{ fontSize: 14 }}>
                        <img
                          src={atleta.usuario?.imagemUrl || '/assets/images/user.png'}
                          alt={nome}
                          className="rounded-circle me-2"
                          width={28}
                          height={28}
                          loading="lazy"
                          style={{ objectFit: 'cover' }}
                        />
                        <span>{nome}</span>
                      </div>
                      <div className="d-flex flex-column align-items-end">
                        {selecionado ? (
                          <span className="badge bg-secondary text-black">Selecionado</span>
                        ) : emUso ? (
                          <span className="badge bg-light text-muted border">Em uso</span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            aria-label={`Selecionar atleta ${nome}`}
                            data-testid={`selecionar-atleta-${atleta.id}`}
                            onClick={() => onSelect(atleta)}
                          >
                            Selecionar
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
                {!filtered.length && (
                  <li className="list-group-item">Nenhum atleta encontrado.</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AthleteSelectorDialog;
